<script>
// Components
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config.json";
import UserProfile from "@/router/views/users/user-profile"

// Helpers
import {authComputed} from "@/state/helpers";
import DateUtils from '@/helpers/date';
import UserUtils from '@/helpers/users';
import HelperUtils from '@/helpers/helper';
import Storage from "@/helpers/storage"

// Services
import GeneratorService from "@/services/GeneratorService";
import MarketingDataService from "@/services/MarketingDataService";
import UsersDataService from "@/services/UsersDataService"

// External Components
import DatePicker  from "vue2-datepicker";
import 'vue2-datepicker/index.css';
import {VueTabs, VTab} from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css';
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';

// Lib
import isEmpty from "is-empty";

const ads = {};
const marketing = {};
const organic = {};
const adsData1 = {};
const adsData2 = {};

export default {
  name: "generator",
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Generator Controller",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },

  components: {
    Layout,
    PageHeader,
    DatePicker,
    VueTabs,
    VTab,
    UserProfile,
    CoolLightBox,
  },

  computed: {
    ...authComputed
  },

  methods: {
    ...UserUtils,
    ...DateUtils,
    ...HelperUtils,

    handlePageChange(value) {
      this.page = value;
      if (this.generatorSearch === 'filters')
        this.loadList();
      else if (this.generatorSearch === 'search')
        this.processSearch(false);
    },

    handleDisplayChange() {
      this.loadList();
    },

    handleDateChange() {
      let now = new this.$moment();
      switch(this.filters.dates.selected) {
        case "always":
          this.filters.fromDate = new this.$moment('2021-01-01').format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "today":
          this.filters.fromDate = now.format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "yesterday":
          this.filters.fromDate = now.subtract(1, 'day').format('YYYY-MM-DD');
          this.filters.toDate = this.filters.fromDate;
          break;
        case "week":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(1, 'week').startOf('day').format('YYYY-MM-DD');
          break;
        case "curMonth":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('month').format('YYYY-MM-DD');
          break;
        case "last30Day":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(30, 'day').startOf('day').format('YYYY-MM-DD');
          break;
        case "lastMonth":
          this.filters.fromDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.filters.toDate = now.endOf('month').format('YYYY-MM-DD');
          break;
        case "last3Month":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(3, 'month').startOf('day').format('YYYY-MM-DD');
          break;
        case "curYear":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('year').format('YYYY-MM-DD');
          break;
        case "custom":
          this.filters.fromDate = null;
          this.filters.toDate = null;
          break;
      }
    },

    getRequestParams() {
      const params = {};

      params["page"] = this.page - 1;
      params["size"] = this.pageSizes.selected;

      if (this.filters.dates.selected === 'custom' && this.filters.dates.dateRange !== null) {
        this.filters.fromDate = this.$moment(this.filters.dates.dateRange[0]).format("YYYY-MM-DD 00:00:00");
        this.filters.toDate = this.$moment(this.filters.dates.dateRange[1]).format("YYYY-MM-DD 23:59:59");
      }
      else if (this.filters.fromDate === null)
        this.handleDateChange();

      if (this.filters.toDate !== null)
        params['toDate'] = this.filters.toDate;

      if (this.filters.fromDate !== null)
        params['fromDate'] = this.filters.fromDate;

      params['gender'] = this.filters.genders.selected;
      params['types'] = this.filters.types.selected;
      params['slGroup'] = this.filters.slGroup.selected;
      params['status'] = this.filters.status.selected;
      params['organic'] = this.filters.organic.selected;
      params['subscribed'] = this.filters.subscribed.selected;
      params['countries'] = this.filters.countries.selected;
      if (this.filters.adsData3.selected !== 'all') {
        params['ads'] = this.filters.adsData3.selected;
      }
      else if (this.filters.adsData2.selected !== 'all') {
        params['ads'] = this.filters.adsData2.selected;
      }
      else if (this.filters.adsData1.selected !== 'all') {
        params['ads'] = this.filters.adsData1.selected;
      }
      else {
        params['ads'] = this.filters.ads.selected;
      }

      if (this.$store.state.layout.timezone === 'utc') {
        params['tz'] = this.$moment.tz.guess();
      }

      return params;
    },

    loadStats() {
      const params = this.getRequestParams();
      GeneratorService
          .getStats(params)
          .then(response => {

            const {total, types, historyGraph} = response.data;
            this.stats.success = total.success;
            this.stats.waiting = total.waiting;
            this.stats.errors = total.errors;
            this.stats.empty = total.empty;
            this.stats.processed = total.processed;

            this.typesGraph.series = types.series;
            this.typesGraph.chartOptions = {...this.typesGraph.chartOptions, ...{
                labels: types.labels
              }};

            this.$refs.generatorHistoryChart.updateSeries([{
              name: "Waiting",
              type: 'bar',
              data: historyGraph.waiting
            },{
              name: "Success",
              type: 'bar',
              data: historyGraph.success
            }, {
              name: "Empty",
              type: 'bar',
              data: historyGraph.empty
            }, {
              name: "Nothing",
              type: 'bar',
              data: historyGraph.processed
            }, {
              name: "Errors",
              type: 'bar',
              data: historyGraph.errors
            }]);
          })
          .catch(error => {
            window.console.error(error);
          });
    },

    loadList() {
      this.isLoading = true;
      const params = this.getRequestParams();
      this.generatorSearch = 'filters';
      GeneratorService
          .getList(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.scheduled = items;
            this.count = totalItems;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("generator.errorGeneratorNoResult"));
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    resetData() {
      this.scheduled = [];
      this.stats = {
        waiting: '-',
        success: '-',
        errors: '-',
        empty: '-',
        processed: '-',
      };
    },

    processFilters() {
      this.resetData();
      this.loadList();
      this.loadStats();
    },

    getFilters() {
      MarketingDataService.getAdsNonOrganicFilters()
          .then(response => {
            marketing.ads = response.data.filters.ads;
            response.data.filters.ads.forEach(ad => {
              this.filters.ads.options.push(ad);
            });
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(this.$t("marketing.getAdsNonOrganicFilters"));
          });
    },

    processSearch(resetPage) {
      if (isEmpty(this.filters.searchType)) {
        this.$toast.error(this.$t("generator.error_searchType"));
        return;
      }

      if (isEmpty(this.filters.searchData)) {
        this.$toast.error(this.$t("generator.error_searchData"));
        return;
      }

      this.generatorSearch = 'search';
      if (resetPage) {
        this.page = 1;
      }

      this.isLoading = true;
      const params = {
        type: this.filters.searchType.selected,
        data: this.filters.searchData,
        page: this.page - 1,
        size: this.pageSizes.selected,
      };

      GeneratorService
          .search(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.scheduled = items;
            this.count = totalItems;
            this.isLoading = false;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("generator.errorGeneratorNoResult"));
            this.isLoading = false;
          });
    },

    getMediaThumb() {
      if (this.scheduled.length > 0)
        this.scheduled.forEach(row => {
          if (row.photo && row.photo.length > 0)
            Storage.getUserMedia(row.userId, row.photo, 'generator-user-'+row.scheduleId);

          if (row.likerPhoto && row.likerPhoto.length > 0)
            Storage.getUserMedia(row.likerId, row.likerPhoto, 'generator-user-'+row.scheduleId+"-liker");

        });
    },

    _isEmpty(val) {
      return isEmpty(val);
    },

    searchUser(userId) {
      this.filters.searchType.selected = 'userId';
      this.filters.searchData = userId;
      this.processSearch(true);
    },

    showContextMenu(event, user) {
      this.$refs.generatorContextMenu.showMenu(event, user)
    },

    processContextMenu(event) {
      const { item, option } = event;
      switch (option.value) {
        case "show":
          this.showUser(item.userId);
          break;

        case "search":
          this.searchUser(item.userId);
          break;
      }
    },

    /* User profile */
    openUserPane() {
      if (!this.isUserPaneDisplayed)
        this.isUserPaneDisplayed = true;
    },

    closeUserPane() {
      this.userTabsActive = '';
      this.isUserPaneDisplayed = false;
    },

    async showUser(userId) {
      const isUserAlreadyDisplayed = this.userTabs.findIndex(x => x.userId === userId);
      let forceShow = false;
      if (isUserAlreadyDisplayed === -1) {
        try {
          const result = await UsersDataService.get(userId);
          if (typeof result.data === 'undefined')
            throw new Error('unable to find user '+userId);

          this.userTabs.push(UserUtils.normalize(result.data));
          forceShow = true;
        }
        catch (error) {
          window.console.error(error);
          this.$toast.error(this.$t("global_error"));
        }
      }

      this.openUserPane();

      if (forceShow || (!forceShow && this.userTabsActive !== userId)) {
        this.userTabsActive = '';
        setTimeout(() => { this.userTabsActive = userId; }, 100);
      }
    },

    showUserMedia(media, index) {
      this.userMedia.media = media;
      this.userMedia.index = index;
    },

    removeUserTab(userId) {
      const index = this.userTabs.findIndex(x => x.userId === userId);
      if (index > -1)
        this.userTabs.splice(index, 1);

      if (this.userTabs.length === 0)
        this.closeUserPane();
    }
  },

  mounted() {
    this.getFilters();
    this.processFilters();
  },

  watch: {
    "filters.organic.selected": function (newVal, oldVal) {
      if (oldVal === newVal)
        return;

      if (newVal === "organic") {
        this.filters.ads.selected = 'all';
        this.filters.ads.options = [{
          value: 'all', text: 'All Ads'
        }];
        if (isEmpty(organic)) {
          MarketingDataService.getAdsOrganicFilters()
              .then(response => {
                response.data.filters.ads.forEach(ad => {
                  this.filters.ads.options.push(ad);
                });

                organic.ads = response.data.filters.ads;
              })
              .catch(error => {
                window.console.log(error);
                this.$toast.error(this.$t("marketing.getAdsOrganicFilters"));
              });
        }
        else {
          organic.ads.forEach(ad => {
            this.filters.ads.options.push(ad);
          });
        }
      }
      else if (['marketing', 'all'].includes(newVal) && oldVal === "organic") {
        this.filters.ads.selected = 'all';
        this.filters.ads.options = [{
          value: 'all', text: 'All Ads'
        }];
        if (!isEmpty(marketing)) {
          marketing.ads.forEach(ad => {
            this.filters.ads.options.push(ad);
          });
        }
      }
    },
    "filters.ads.selected": function (newVal, oldVal) {
      if (oldVal === newVal || newVal === 'all')
        return;

      this.filters.adsData1.selected = 'all';
      this.filters.adsData2.selected = 'all';
      this.filters.adsData3.selected = 'all';
      this.filters.adsData1.options = [{
        value: 'all', text: 'All Data1'
      }];
      this.filters.adsData2.options = [{
        value: 'all', text: 'All Data2'
      }];
      this.filters.adsData3.options = [{
        value: 'all', text: 'All Data3'
      }];

      if (isEmpty(ads[newVal])) {
        MarketingDataService.getAdsNonOrganicData1Filters(newVal)
            .then(response => {
              response.data.filters.ads.forEach(ad => {
                this.filters.adsData1.options.push(ad);
              });

              ads[newVal] = response.data.filters.ads;
            })
            .catch(error => {
              window.console.log(error);
              this.$toast.error(this.$t("marketing.getAdsNonOrganicData1Filters"));
            });
      }
      else {
        ads[newVal].forEach(ad => {
          this.filters.adsData1.options.push(ad);
        });
      }
    },
    "filters.adsData1.selected": function (newVal, oldVal) {
      if (oldVal === newVal || newVal === 'all')
        return;

      this.filters.adsData2.selected = 'all';
      this.filters.adsData3.selected = 'all';
      this.filters.adsData2.options = [{
        value: 'all', text: 'All Data2'
      }];
      this.filters.adsData3.options = [{
        value: 'all', text: 'All Data3'
      }];

      if (isEmpty(adsData1[newVal])) {
        MarketingDataService.getAdsNonOrganicData2Filters(newVal)
            .then(response => {
              response.data.filters.ads.forEach(ad => {
                this.filters.adsData2.options.push(ad);
              });

              adsData1[newVal] = response.data.filters.ads;
            })
            .catch(error => {
              window.console.log(error);
              this.$toast.error(this.$t("marketing.getAdsNonOrganicData2Filters"));
            });
      }
      else {
        adsData1[newVal].forEach(ad => {
          this.filters.adsData2.options.push(ad);
        });
      }
    },
    "filters.adsData2.selected": function (newVal, oldVal) {
      if (oldVal === newVal || newVal === 'all')
        return;

      this.filters.adsData3.selected = 'all';
      this.filters.adsData3.options = [{
        value: 'all', text: 'All Data3'
      }];

      if (isEmpty(adsData2[newVal])) {
        MarketingDataService.getAdsNonOrganicData3Filters(newVal)
            .then(response => {
              response.data.filters.ads.forEach(ad => {
                this.filters.adsData3.options.push(ad);
              });

              adsData2[newVal] = response.data.filters.ads;
            })
            .catch(error => {
              window.console.log(error);
              this.$toast.error(this.$t("marketing.getAdsNonOrganicData3Filters"));
            });
      }
      else {
        adsData2[newVal].forEach(ad => {
          this.filters.adsData3.options.push(ad);
        });
      }
    }
  },

  data() {
    return {
      isLoading: false,
      title: this.$t("menuitems.system.generator"),
      generatorSearch: 'filters',
      scheduled: [],
      page: 1,
      count: 0,
      pageSizes: {
        options: ['20', '50', '100'],
        selected: '50'
      },
      optionsContext: [
        {
          name: this.$t("show_user"),
          value: 'show'
        },
        {
          name: this.$t("search_user"),
          value: 'search'
        },
      ],
      stats: {
        waiting: '-',
        success: '-',
        errors: '-',
        empty: '-',
        processed: '-',
      },
      typesGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'generator-types',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'bottom'
          },
        }
      },
      historyGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: true,
            type: 'bar',
            toolbar: {
              show: true,
            },
            zoom: {
              enabled: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              endingShape: "rounded",
              borderRadius: 10,
              dataLabels: {
                total: {
                  enabled: true,
                  style: {
                    fontSize: '13px',
                  }
                }
              }
            },
          },
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            shared: true,
            intersect: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          colors: ["#f8bd25", "#34c38f", "#666666", "#556ee6", "#f46a6a"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },
      filters: {
        searchType: {
          selected: 'userId',
          options: [
            {value:'userId', text:this.$t("userId")},
            {value:'email', text:this.$t("email")}
          ]
        },
        searchData: "",
        fromDate: null,
        toDate: null,
        dates: {
          dateRange: null,
          selected: 'week',
          options: [
            {value:'today', text:this.$t("dates.today")},
            {value:'yesterday', text:this.$t("dates.yesterday")},
            {value:'week', text:this.$t("dates.week")},
            {value:'curMonth', text:this.$t("dates.curMonth")},
            {value:'last30Day', text:this.$t("dates.last30Day")},
            {value:'lastMonth', text:this.$t("dates.lastMonth")},
            {value:'last3Month', text:this.$t("dates.last3Month")},
            {value:'curYear', text:this.$t("dates.curYear")},
            {value:'always', text:this.$t("dates.always")},
            {value:'custom', text:this.$t("dates.custom")}
          ]
        },
        genders: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("users.all_genders")},
            {value: 1, text: this.$t("users.search.genderMale")},
            {value: 2, text: this.$t("users.search.genderFemale")}
          ]
        },
        subscribed: {
          selected: 'all',
          options: [
            {value: 'all', text: "All Subscribe Status"},
            {value: 0, text: "No Subscribed"},
            {value: 1, text: "Only Subscribed"},
          ]
        },
        slGroup: {
          selected: 'all',
          options: [
            {value: 'all', text: "All SLGroups"},
            {value: "with", text: "SLGroup"},
            {value: "without", text: "NO SLGroup"}
          ]
        },
        organic: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All Source'},
            {value: 'organic', text: 'Only Organic'},
            {value: 'marketing', text: 'Only Marketing'}
          ]
        },
        ads: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Ads'
          }]
        },
        adsData1: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Data1'
          }]
        },
        adsData2: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Data2'
          }]
        },
        adsData3: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Data3'
          }]
        },
        types: {
          selected: 'all',
          options: [
              {value: 'all', text: 'All Types'},
              {value: 'likes', text: 'Likes'},
              {value: 'iceBreakers', text: 'IceBreakers'},
              {value: 'videoLikes', text: 'VideoLikes'},
              {value: 'photos', text: 'Add Photo'}
          ]
        },
        countries: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All Countries'},
            {value: '', text: '----------------'},
            {value: 'FR', text: 'FR - France'},
            {value: 'BE', text: 'BE - Belgium'},
            {value: 'CH', text: 'CH - Switzerland'},
            {value: 'CA', text: 'CA - Canada'},
            {value: '', text: '----------------'},
            {value: 'others', text: 'Others'},
          ]
        },
        // W: Waiting I: Inserted S: Success p: Processed E: Empty, NP: No Photo, EL: Empty Location, EF: Empty Finish, ERR: Error
        status: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("generator.status_all")},
            {value: 'GS', text: this.$t("generator.status_GS")},
            {value: 'W', text: this.$t("generator.status_W")},
            {value: 'P', text: this.$t("generator.status_P")},
            {value: 'NP', text: this.$t("generator.status_NP")},
            {value: 'E', text: this.$t("generator.status_E")},
            {value: 'EL', text: this.$t("generator.status_EL")},
            {value: 'EF', text: this.$t("generator.status_EF")},
            {value: 'I', text: this.$t("generator.status_I")},
            {value: 'GE', text: this.$t("generator.status_GE")},
            {value: 'ERR', text: this.$t("generator.status_ERR")},
          ]
        }
      },
      /* User profile */
      isUserPaneDisplayed: false,
      userTabs:[],
      userTabsActive: '',
      userMedia: {
        media: [],
        index: -1
      },
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">
      <b-button
          class="position-fixed"
          style="top: 77px; right: 15px;z-index:150"
          v-show="!isUserPaneDisplayed && userTabs.length > 0"
          @click="openUserPane"
          variant="outline-primary">
        <i class="bx bxs-cog"></i> Show Panel
      </b-button>

      <!-- Filters -->
      <div class="card">
        <div class="card-body">
          <!-- Search -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="generatorSearchForm" @submit.stop.prevent>
            <div class="col-sm-auto">
              <b-form-select
                  id="generatorSearchTypeFilter"
                  v-model="filters.searchType.selected"
                  class="form-select me-lg-1"
                  :options="filters.searchType.options">
              </b-form-select>
            </div>
            <div class="col-sm-auto">
              <b-form-input
                  id="generatorSearchData"
                  class="me-lg-1"
                  v-model="filters.searchData"
                  placeholder="Search…">
              </b-form-input>
            </div>
            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processSearch(true)">{{ $t("searchBtn") }}</b-button>
            </div>
          </b-form>
          <hr />
          <!-- filters -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="generatorFiltersForm" @submit.stop.prevent>

            <!-- dates -->
            <div class="col-sm-auto d-flex">
              <b-form-select
                  id="generatorDatesFilter"
                  @change="handleDateChange"
                  v-model="filters.dates.selected"
                  class="form-select me-lg-1"
                  :options="filters.dates.options">
              </b-form-select>
              <date-picker
                  v-show="filters.dates.selected === 'custom'"
                  :lang="page.localePicker"
                  :placeholder="this.$t('choose_date_interval')"
                  type="date"
                  value-type="format"
                  show-week-number
                  v-model="filters.dates.dateRange"
                  class="me-lg-2"
                  range append-to-body ></date-picker>

            </div>

            <!-- Countries -->
            <div class="col-sm-auto">
              <b-form-select
                  id="generatorCountriesFilter"
                  v-model="filters.countries.selected"
                  class="form-select me-lg-1"
                  :options="filters.countries.options">
              </b-form-select>
            </div>

            <!-- Genders -->
            <div class="col-sm-auto">
              <b-form-select
                  id="generatorGenderFilter"
                  v-model="filters.genders.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.genders.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Types -->
            <div class="col-sm-auto">
              <b-form-select
                  id="generatorTypeFilter"
                  v-model="filters.types.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.types.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Subscribed -->
            <div class="col-sm-auto">
              <b-form-select
                  id="generatorSubscribedFilter"
                  v-model="filters.subscribed.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.subscribed.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- SlGroups -->
            <div class="col-sm-auto">
              <b-form-select
                  id="generatorSlGroupFilter"
                  v-model="filters.slGroup.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.slGroup.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Status -->
            <div class="col-sm-auto">
              <b-form-select
                  id="generatorStatusFilter"
                  v-model="filters.status.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="st in filters.status.options" :key="st.value" :value="st.value">{{ st.text }}</option>
              </b-form-select>
            </div>

            <!-- Organic -->
            <div class="col-sm-auto">
              <b-form-select
                  id="generatorOrganicFilter"
                  v-model="filters.organic.selected"
                  class="form-select me-lg-1"
                  :options="filters.organic.options">
              </b-form-select>
            </div>

            <!-- Ads -->
            <div class="col-sm-auto">
              <b-form-select
                  id="generatorAdsFilter"
                  v-model="filters.ads.selected"
                  class="form-select me-lg-1"
                  :options="filters.ads.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto" v-show="filters.ads.selected !== 'all'">
              <b-form-select
                  id="generatorAdsData1Filter"
                  v-model="filters.adsData1.selected"
                  class="form-select me-lg-1"
                  :options="filters.adsData1.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto" v-show="filters.adsData1.selected !== 'all'">
              <b-form-select
                  id="generatorAdsData2Filter"
                  v-model="filters.adsData2.selected"
                  class="form-select me-lg-1"
                  :options="filters.adsData2.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto" v-show="filters.adsData2.selected !== 'all'">
              <b-form-select
                  id="generatorAdsData3Filter"
                  v-model="filters.adsData3.selected"
                  class="form-select me-lg-1"
                  :options="filters.adsData3.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processFilters">{{ $t("find") }}</b-button>
            </div>
          </b-form>
        </div>
      </div>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <!-- Stats Plain -->
      <div class="row" v-if="generatorSearch === 'filters'">
        <!-- Success -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("generator.success") }}</p>
                  <h4 class="mb-0">{{ stats.success }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-success">
                    <i class="bx bx-check font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Waiting -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("generator.waiting") }}</p>
                  <h4 class="mb-0">{{ stats.waiting }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-warning">
                    <i class="bx bx-check font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Empty -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("generator.empty") }}</p>
                  <h4 class="mb-0">{{ stats.empty }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-dark">
                    <i class="bx bx-info-circle font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Processed -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-muted fw-medium mb-2">{{ $t("generator.processed") }}</p>
                  <h4 class="mb-0">{{ stats.processed }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-primary">
                    <i class="bx bx-help-circle font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Error -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-muted fw-medium mb-2">{{ $t("generator.errors") }}</p>
                  <h4 class="mb-0">{{ stats.errors }}</h4>
                </div>

                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-danger">
                    <i class="bx bx-error font-size-24"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="generatorSearch === 'filters'">

        <!-- graph Types -->
        <div class="col-12 col-lg-3">
          <div class="card">
            <div class="card-body" id="cardChartTypes">
              <h5 class="card-title">Types</h5>
              <apexchart
                  ref="generatorTypesChart"
                  height="300"
                  type="donut"
                  dir="ltr"
                  :series="typesGraph.series"
                  :options="typesGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <!-- History -->
        <div class="col-12 col-lg-9">
          <div class="card">
            <div class="card-body" id="cardChartGeneratorHistory">
              <h5 class="card-title">History</h5>
              <apexchart
                  ref="generatorHistoryChart"
                  class="apex-charts"
                  type="bar"
                  dir="ltr"
                  height="300"
                  :series="historyGraph.series"
                  :options="historyGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

      </div>

      <!-- Top list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>

          {{ count }} {{ $t('resultsCount') }}
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="generator-list"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Result list -->
      <div class="row bg-white" v-if="!isLoading && scheduled.length > 0">
        <div class="table-responsive card-body">
          <table
              id="generator-list"
              class="table project-list-table table-nowrap table-centered table-borderless align-middle"
          >
            <thead>
            <tr>
              <th scope="col">{{ $t('generator.scheduledAt') }}</th>
              <th scope="col">{{ $t('generator.status') }}</th>
              <th scope="col">{{ $t('generator.type') }}</th>
              <th scope="col">{{ $t('generator.user') }}</th>
              <th scope="col">{{ $t('generator.liker') }}</th>
              <th scope="col">Diff.</th>
              <th scope="col">SLGroup</th>
              <th scope="col">Err.</th>
              <th scope="col">{{ $t('action') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in scheduled" :key="row.scheduleId" @contextmenu.prevent.stop="showContextMenu($event, row)">
              <td>
                <p class="text-muted font-size-12">{{ formatDateString(row.scheduledAt, 'DD MMM YYYY HH:mm') }}</p>
              </td>
              <td>
                <span
                    class="badge"
                    :class="{
                      'bg-success text-light': `${row.processed}` === 'GS',
                      'bg-info text-light': `${row.processed}` === 'NP',
                      'bg-danger text-light': `${row.processed}` === 'ERR' || `${row.processed}` === 'GE',
                      'bg-light text-dark': `${row.processed}` === 'E' || `${row.processed}` === 'EF' || `${row.processed}` === 'EL' || `${row.processed}` === 'P',
                      'bg-warning text-dark': `${row.processed}` === 'W',
                    }"
                >{{ $t(`generator.status_${row.processed}`) }}</span>
              </td>
              <td>
                <p class="text-muted font-size-12">
                  <strong>{{ row.scheduleType }}</strong>
                  <br>
                  <span
                      class="badge"
                      :class="{
                      'bg-pink text-light': `${row.subscribed}` === '1',
                      'bg-light text-dark': `${row.subscribed}` === '0',
                    }"
                  >{{ $t(`generator.status_subscribed_${row.subscribed}`) }}</span>
                </p>
              </td>
              <td>
                <div class="media">
                  <div class="avatar-xs">
                    <img
                        :id="`generator-user-${row.scheduleId}`"
                        :src="require('@/assets/images/users/default-user.png')"
                        class="d-flex rounded-circle avatar-xs" />
                  </div>
                  <div class="media-body ms-1">
                    <h5 class="font-size-14 mb-0">
                      <span
                          :class="{
                          'text-primary': row.sex === 1,
                          'text-danger': row.sex === 2,
                          'text-dark': row.sex === 0,
                        }"
                      >
                        {{ row.name || '-' }}
                      </span> {{ getAge(row.birth) }}</h5>
                    <small class="text-muted">#{{ row.userId }}</small>
                  </div>
                </div>
              </td>
              <td>
                <div class="media" v-show="row.likerId !== null ">
                  <div class="avatar-xs">
                    <img
                        :id="`generator-user-${row.scheduleId}-liker`"
                        :src="require('@/assets/images/users/default-user.png')"
                        class="d-flex rounded-circle avatar-xs" />
                  </div>
                  <div class="media-body ms-1">
                    <h5 class="font-size-14 mb-0">
                      <span
                          :class="{
                          'text-primary': row.likerSex === 1,
                          'text-danger': row.likerSex === 2,
                          'text-dark': row.likerSex === 0,
                        }"
                      >
                        {{ row.likerName || '-' }}
                      </span> {{ getAge(row.likerBirth) }}</h5>
                    <small class="text-muted">#{{ row.likerId }}</small>
                  </div>
                </div>
              </td>
              <td>
                <b>{{ getDistanceFromLatLng(row.userLat, row.userLng, row.likerLat, row.likerLng) }}</b> km<br>
                <b>{{ getAgeDiff(row.birth, row.likerBirth) }}</b> ans
              </td>
              <td>
                <p v-show="row.slGroup === 1">YES</p>
              </td>
              <td>
                <p v-show="!_isEmpty(row.error)">{{ row.error }}</p>
              </td>
              <td>
                <b-dropdown
                    class="card-drop"
                    variant="white"
                    menu-class="dropdown-menu-end"
                    right
                    toggle-class="p-0"
                >
                  <template v-slot:button-content>
                    <i class="mdi mdi-dots-horizontal font-size-18"></i>
                  </template>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="showUser(row.userId)"
                  >{{ $t("show_user") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="searchUser(row.userId)"
                  >{{ $t("search_user") }}</b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Result Empty -->
      <div class="row" v-if="!isLoading && scheduled.length === 0">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Empty Result</h4>
              <p>No Schedule were found for these filters or search. Try again.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="generator-list"
            ></b-pagination>
          </div>
        </div>
      </div>

    </div>
    <b-sidebar id="sidebar-right" right v-model="isUserPaneDisplayed">
      <vue-tabs
          v-model="userTabsActive"
          active-tab-color="#213213"
          active-text-color="white">
        <v-tab v-for="userTab in userTabs" :key="userTab.userId" :title="userTab.userId" >
          <div slot="title">{{ userTab.name }} <i @click.stop="removeUserTab(userTab.userId)" class="bx bx-x-circle" ></i></div>
          <UserProfile
              :userId="null"
              :user="userTab"
              v-on:SHOW_MEDIA="showUserMedia">
          </UserProfile>
        </v-tab>
      </vue-tabs>
    </b-sidebar>

    <vue-simple-context-menu
        element-id="generatorContextMenu"
        :options="optionsContext"
        ref="generatorContextMenu"
        @option-clicked="processContextMenu"
    />

    <CoolLightBox
        :items="userMedia.media"
        :index="userMedia.index"
        :effect="'fade'"
        @close="userMedia.index = null">
    </CoolLightBox>

  </Layout>
</template>

<style>
.b-sidebar-right {
  width:35%;
}

.b-sidebar-body {
  padding:16px;
}
</style>
